import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import DragHandleHorizontalIcon from '@atlaskit/icon/core/drag-handle-horizontal';
import { type Identifier } from '@atlaskit/media-client';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Box, Flex, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AttachmentViewer from '@databases/attachments/AttachmentViewer';
import { type DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import React, { useCallback, useEffect, useRef } from 'react';
import { type DropzoneRef } from 'react-dropzone';
import invariant from 'tiny-invariant';

const dragHandleStyles = xcss({
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-50%)',
  transition: 'opacity 0.2s ease-in-out',
  zIndex: 'tooltip'
});

const gridCardStyles = xcss({
  position: 'relative',
  border: `${token('border.width.selected', '1px')} solid transparent`,
  borderRadius: 'radius.small',
  background: 'color.background.neutral.subtle',
  overflow: 'visible',
  boxShadow: 'elevation.shadow.raised',
  width: '100%',
  height: '220px',
  padding: 'space.050',
  cursor: 'pointer',
  zIndex: 'card'
});

const gridCardContentStyles = xcss({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: 'radius.xsmall'
});

const imageContainerStyles = xcss({
  flex: '1',
  minHeight: '0',
  maxHeight: '150px',
  overflow: 'hidden',
  width: '100%'
});

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];

interface GridItemData {
  [key: string]: unknown;
  [key: symbol]: boolean;
  index: number;
  instanceId: symbol;
}

interface GridItemProps {
  value: Value;
  index: number;
  fileNames: Record<string, string>;
  handleChangeFile: (file: Value, index: number) => void;
  onDropNewFile: (file: File, index: number) => Promise<string | undefined>;
  handleDelete: (index: number) => void;
  contentId: string;
  uploadOrder: number[];
  getMediaFiles: () => Identifier[];
  mediaFormat: any;
  instanceId: symbol;
  draggingItemId: string | null;
  setDraggingItemId: (id: string | null) => void;
  dropzoneRef: React.RefObject<DropzoneRef>;
  gridItemKey: symbol;
  getGridItemData: (params: {
    item: Value;
    index: number;
    instanceId: symbol;
  }) => GridItemData;
  isSelected: boolean;
  onSelect: (id: string) => void;
  browseOrDropButtonRef?: React.MutableRefObject<HTMLButtonElement | null>;
}

const GridItem = React.memo(
  ({
    value,
    index,
    fileNames,
    handleDelete,
    contentId,
    uploadOrder,
    getMediaFiles,
    mediaFormat,
    instanceId,
    setDraggingItemId,
    dropzoneRef,
    gridItemKey,
    getGridItemData,
    isSelected,
    onSelect,
    browseOrDropButtonRef
  }: GridItemProps) => {
    const gridItemRef = useRef<HTMLDivElement>(null);
    const dragHandleRef = useRef<HTMLButtonElement>(null);

    const fileId = 'fileId' in value ? value.fileId : '';
    const displayName =
      fileNames[fileId] ||
      ('filename' in value ? value.filename : fileId || 'Attachment');

    useEffect(() => {
      const el = gridItemRef.current;
      invariant(el);
      invariant(dragHandleRef.current);

      return combine(
        draggable({
          element: el,
          dragHandle: dragHandleRef.current,
          getInitialData: () => getGridItemData({ item: value, index, instanceId }),
          onDragStart: () => {
            setDraggingItemId(value._id);
            if (el) {
              el.style.isolation = 'isolate';
              el.style.position = 'relative';
            }
          },
          onDrop: () => {
            setDraggingItemId(null);
            if (el) {
              el.style.isolation = '';
              el.style.position = '';
            }
          }
        }),
        dropTargetForElements({
          element: el,
          canDrop({ source }) {
            return (
              source.data &&
              typeof source.data === 'object' &&
              gridItemKey in source.data &&
              source.data.instanceId === instanceId &&
              source.data._id !== value._id
            );
          },
          getData({ input }) {
            return attachClosestEdge(
              getGridItemData({ item: value, index, instanceId }),
              {
                element: el,
                input,
                allowedEdges: ['top', 'bottom', 'left', 'right']
              }
            );
          }
        })
      );
    }, [index, value, instanceId, setDraggingItemId, getGridItemData, gridItemKey]);

    const handleCardClick = useCallback(() => {
      onSelect(value._id);
    }, [onSelect, value._id]);

    const handleCloseClick = useCallback(() => {
      handleDelete(index);
      browseOrDropButtonRef?.current?.focus();
    }, [handleDelete, index, browseOrDropButtonRef]);

    return (
      <Box
        xcss={gridCardStyles}
        style={{
          backgroundColor: isSelected
            ? token('color.background.selected.hovered')
            : undefined,
          borderColor: isSelected ? token('color.border.accent.blue') : 'transparent'
        }}
        onClick={handleCardClick}
        ref={gridItemRef}
        id={`grid-item-${value._id}`}
      >
        <Box
          xcss={dragHandleStyles}
          ref={dragHandleRef}
          style={{
            opacity: isSelected ? 1 : 0
          }}
        >
          <IconButton
            icon={(iconProps) => <DragHandleHorizontalIcon {...iconProps} size="small" />}
            spacing="compact"
            appearance="primary"
            label={''}
          />
        </Box>

        <Flex direction="column" xcss={gridCardContentStyles} gap="space.075">
          <Flex justifyContent="end" alignItems="center">
            <IconButton
              icon={(iconProps) => <CrossIcon {...iconProps} size="medium" />}
              appearance="subtle"
              label=""
              onClick={handleCloseClick}
              testId="remove-attachment-button"
            />
          </Flex>

          <Flex justifyContent="center" alignItems="center" xcss={imageContainerStyles}>
            <AttachmentViewer
              dropzoneRef={dropzoneRef}
              contentId={contentId}
              value={value}
              handleDelete={() => handleDelete(index)}
              showSpinner={uploadOrder.includes(index)}
              mediaFiles={getMediaFiles()}
              mediaFormat={mediaFormat}
              attachmentLocation="edit"
            />
          </Flex>

          <Text
            size="small"
            maxLines={2}
            align="center"
            color={isSelected ? 'color.text.selected' : 'color.text'}
          >
            {displayName}
          </Text>
        </Flex>
      </Box>
    );
  }
);

GridItem.displayName = 'GridItem';

export default GridItem;
